import { Observable, race, timer } from 'rxjs'
import { filter, map, take } from 'rxjs/operators'
import { PageVisibilityService } from '../app/services/page-visibility.service'

// When tab is hidden, all polling intervals are clamped to at least this value.
// Matches Application.BACKGROUND_POLL_INTERVAL so hidden tabs poll no faster than standby.
const THROTTLED_INTERVAL = 60000

/**
 * Drop-in replacement for `timer(intervalMs)` that is page-visibility-aware.
 *
 * - Tab visible: behaves identically to `timer(intervalMs)`
 * - Tab hidden: waits `max(intervalMs, 60s)`, OR fires immediately when the tab
 *   becomes visible again (whichever comes first)
 *
 * Falls back to plain `timer()` if PageVisibilityService hasn't been instantiated yet.
 */
export function visibilityAwareTimer(intervalMs: number): Observable<0> {
  const svc = PageVisibilityService.instance
  // Safe fallback: if the service hasn't been bootstrapped yet, use a plain timer
  if (!svc) {
    return timer(intervalMs) as Observable<0>
  }

  if (svc.isVisible) {
    // Tab is visible — use the requested interval with zero overhead.
    // If the tab goes hidden mid-wait we don't need to react; the NEXT
    // cycle's call to visibilityAwareTimer will pick up the hidden state.
    return timer(intervalMs) as Observable<0>
  }

  // Tab is hidden — clamp the interval to at least THROTTLED_INTERVAL
  const effectiveInterval = Math.max(intervalMs, THROTTLED_INTERVAL)

  // Race: either the throttled timer fires, or the tab becomes visible first.
  // When the tab becomes visible, the race resolves immediately so the caller
  // gets a fresh poll right away (instant UI refresh on tab-return).
  const timerFire$ = timer(effectiveInterval)
  const tabBecameVisible$ = svc.visible$.pipe(
    filter((visible) => visible),
    take(1),
    map(() => 0 as const)
  )

  return race(timerFire$, tabBecameVisible$) as Observable<0>
}
